/**
 * A munkamenet állapota. A JOGOSULTSÁGRÓL itt nem születik döntés — azt a
 * szerver mondja meg minden kérésnél. A felület csak megjeleníti, amit a
 * `/auth/me` visszaad.
 *
 * Ez szándékos: ha a kliens döntené el, mit lát, a szűkítés a kliensen múlna.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { capturePrincipal, clearAllSessions } from '@/features/capture/session-storage';
import { ApiError, api, type LoginResponse, type MagicLinkCheck, type Me } from '@/lib/api-client';

/**
 * Miért nincs többé munkamenet ezen a lapon (#76):
 *   • `ended` — a maszk lezárult (kilépés, lejárat, visszavonás);
 *   • `switched` — ugyanebben a böngészőben időközben MÁS lépett be (egy
 *     másik maszk, vagy az ügyfél maga). A süti böngészőnként egy: ez a lap
 *     a régi képernyővel, de az új identitás sütijével dolgozna tovább.
 */
export type SessionEnd = 'ended' | 'switched';

/**
 * A LAPOK KÖZTI JELZÉS (#76). Ha egy lapon új identitás lép be, a többi lap
 * tőle tudja meg — nem a következő kattintásnál, amikor a kérése már az új
 * identitás nevében ment el. Ahol nincs `BroadcastChannel`, a maszk-sáv
 * fókuszos újraellenőrzése marad a háló.
 */
const IDENTITY_CHANNEL = 'fakt.identity';

/**
 * Ennek a lapnak az azonosítója. A csatorna a küldő lap MÁSIK példányának is
 * kézbesít — a saját jelzésére a lap ne kérdezzen újra.
 */
const TAB_ID = Math.random().toString(36).slice(2);

function postIdentityChange(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(IDENTITY_CHANNEL);
  channel.postMessage(TAB_ID);
  channel.close();
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** „Él-e még ez a varázslink, és kié?" — nem használja el a tokent. */
  checkMagicLink: (token: string) => Promise<MagicLinkCheck>;
  /**
   * Beváltás: jelszót állít, és RÖGTÖN be is léptet a friss jelszóval.
   *
   * ⚠️ A `'passwordSet'` NEM HIBA. A jelszó elmentődött és a token elhasználódott,
   * csak a munkamenet nem állt fel (a `/auth/me` bukott). A képernyőnek ezt ki
   * kell mondania: egy újrapróbálás már „lejárt link"-et kapna.
   */
  redeemMagicLink: (token: string, password: string) => Promise<'signedIn' | 'passwordSet'>;
  /** A könyvelő átadó kódjának beváltása (#76) — az admin új lapja hozza. */
  redeemImpersonation: (code: string) => Promise<void>;
  /** Újraolvassa, ki van belépve — a maszk-sáv hívja, hogy a visszavonás látsszon. */
  refresh: () => Promise<Me | null>;
  signOut: () => void;
  /**
   * A MUNKAMENET VÉGE ezen a lapon (#76). A felület ilyenkor NEM a belépő
   * képernyőt mutatja: a könyvelő nem a saját nevén akar belépni az ügyfél
   * felületére, és egy átvett lapon sem szabad csendben továbbdolgozni.
   */
  sessionEnd: SessionEnd | null;
  /** Ha a maszkból kilépés NEM sikerült — a sáv marad, és ezt mondja ki. */
  exitError: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionEnd, setSessionEnd] = useState<SessionEnd | null>(null);
  const [exitError, setExitError] = useState(false);
  const current = useRef<Me | null>(null);
  /**
   * CSAK A LEGUTOLSÓ KÉRDÉS VÁLASZA ÉRVÉNYES (#76). A maszk beváltása az
   * induló `/auth/me` után még egyszer kérdez — ha az első válasz lassabban
   * érne be, felülírná az újat, és a lap a régi (vagy semmilyen) identitással
   * futna tovább.
   */
  const generation = useRef(0);

  const apply = useCallback((next: Me | null) => {
    const previous = current.current;
    if (
      previous !== null &&
      next !== null &&
      capturePrincipal(previous.actor) !== capturePrincipal(next.actor)
    ) {
      // MÁS LÉPETT BE EBBEN A BÖNGÉSZŐBEN — ez a lap nem folytathatja a régi
      // képernyővel. A képernyő eldobása a lényeg: a feltöltés-lista, a nyitott
      // kamera és a kérések mind a régi identitás adatai.
      setSessionEnd('switched');
      void clearAllSessions();
      current.current = null;
      setMe(null);
      return;
    }
    if (previous?.actor.kind === 'impersonation' && next === null) {
      setSessionEnd('ended');
      // Lejáratnál és visszavonásnál is: a könyvelő fotói ne maradjanak az
      // ügyfél eszközén.
      void clearAllSessions();
    }
    current.current = next;
    setMe(next);
  }, []);

  /**
   * A friss `me`-t vissza is adja — a beváltásnak tudnia kell, felállt-e.
   *
   * ⚠️ CSAK A 401 ÉS A 403 JELENTI, HOGY NINCS MUNKAMENET (#76). A maszk-sáv
   * félpercenként és fókuszkor kérdez; egy ébredő, még hálózat nélküli telefon
   * vagy egy 5xx különben egy élő maszkot „lezárult"-ra billentene, miközben a
   * szerveren nyitva marad.
   */
  const refresh = useCallback(async (): Promise<Me | null> => {
    const mine = ++generation.current;
    let next: Me | null;
    try {
      next = await api<Me>('/auth/me');
    } catch (caught) {
      const signedOut =
        caught instanceof ApiError && (caught.status === 401 || caught.status === 403);
      if (!signedOut && current.current !== null) {
        if (mine === generation.current) setLoading(false);
        return current.current;
      }
      next = null;
    }
    if (mine === generation.current) {
      apply(next);
      setLoading(false);
    }
    return next;
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(IDENTITY_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      if (event.data !== TAB_ID) void refresh();
    };
    return () => channel.close();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      // A SZÁNDÉKOS identitásváltás nem „átvett lap".
      current.current = null;
      setSessionEnd(null);
      await refresh();
      postIdentityChange();
    },
    [refresh]
  );

  const checkMagicLink = useCallback(
    (token: string) =>
      api<MagicLinkCheck>('/auth/magic-link/check', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    []
  );

  /**
   * A BEVÁLTÁS UGYANOTT VÉGZŐDIK, AHOL A BELÉPÉS: munkamenet-tokennel és egy
   * friss `/auth/me`-vel. Ezért van itt, és nem a képernyőben — a munkamenet
   * állapotát egyetlen hely írja.
   */
  const redeemMagicLink = useCallback(
    async (token: string, password: string) => {
      await api<LoginResponse>('/auth/magic-link/redeem', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      return (await refresh()) === null ? 'passwordSet' : 'signedIn';
    },
    [refresh]
  );

  /**
   * ⚠️ A KILÉPÉS MOSTANTÓL SZERVER-HÍVÁS (#14). A munkamenet `httpOnly`
   * sütiben van, amit a JS nem tud törölni — csak a szerver `Set-Cookie`-ja.
   * A korábbi alak a kliens példányát törölte, és a szerver nem tudott róla:
   * a token a lejártáig érvényes maradt.
   *
   * A hívás hibáját ELNYELJÜK, és a felület akkor is kilép. Aki ki akar
   * lépni, annak a képernyő nem maradhat nyitva azért, mert a hálózat
   * éppen döglött — a süti a böngésző bezárásáig él, de a felhasználó
   * szándéka egyértelmű volt.
   */
  /**
   * A maszkból kilépés — megvárja a szervert (lásd a `signOut` alatt). Külön
   * függvény, mert a `signOut` felülete szinkron marad: a hívók egy kattintás-
   * kezelőből hívják, és nem kell tudniuk, melyik ág fut.
   */
  const endImpersonation = useCallback(async () => {
    setExitError(false);
    try {
      await api<void>('/auth/impersonation/end', { method: 'POST' });
    } catch (caught) {
      const alreadyGone = caught instanceof ApiError && caught.status === 401;
      if (!alreadyGone) {
        setExitError(true);
        return;
      }
    }
    generation.current++;
    apply(null);
  }, [apply]);

  const signOut = useCallback(() => {
    // Egy épp úton lévő `/auth/me` válasza ne hozza vissza a munkamenetet a
    // kilépés után (a fókusz-újraellenőrzés a kattintás előtt indul el).
    generation.current++;

    if (current.current?.actor.kind !== 'impersonation') {
      void api<void>('/auth/logout', { method: 'POST' }).catch(() => undefined);
      // A félbehagyott fotók az identitással mennek — közös eszközön se
      // maradjanak a következő belépőnek.
      void clearAllSessions();
      apply(null);
      return;
    }

    // MASZKBÓL MÁS AJTÓ, ÉS MEG IS VÁRJUK (#76). A `/auth/logout` maszkból nem
    // érhető el, mert nem naplóz; az `end` a szerveren zárja le a munkamenetet.
    // Ha elbukik, a lap NEM mondhatja, hogy lezárult: a munkamenet a szerveren
    // nyitva van, és egy újratöltés visszahozná. A sáv marad, a hiba látszik.
    void endImpersonation();
  }, [apply, endImpersonation]);

  const redeemImpersonation = useCallback(
    async (code: string) => {
      await api<{ expiresAt: string }>('/auth/impersonation/redeem', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      // CSAK A SIKERES BEVÁLTÁS UTÁN TÖRÖL. Egy elavult vagy hamis link ne
      // vigye el egy ügyfél félbehagyott fotóit — de a sikeres beváltás után az
      // eszközön előzőleg ülő identitás sorozata nem kerülhet a könyvelő elé.
      await clearAllSessions();
      // A SZÁNDÉKOS identitásváltás nem „átvett lap".
      current.current = null;
      setSessionEnd(null);
      setExitError(false);
      await refresh();
      postIdentityChange();
    },
    [refresh]
  );

  const value = useMemo(
    () => ({
      me,
      loading,
      signIn,
      checkMagicLink,
      redeemMagicLink,
      redeemImpersonation,
      refresh,
      signOut,
      sessionEnd,
      exitError,
    }),
    [
      me,
      loading,
      signIn,
      checkMagicLink,
      redeemMagicLink,
      redeemImpersonation,
      refresh,
      signOut,
      sessionEnd,
      exitError,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('A useAuth csak AuthProvider-en belül használható.');
  }
  return context;
}
